/**
 * interactionManager.js - User Interaction Handling
 *
 * Manages:
 * - onPointerDown click handling
 * - Raycaster setup
 * - Event listeners
 * - Decal placement logic
 */

import * as THREE from 'three';
import {
    raycaster,
    camera,
    controls,
    markers,
    markerHelpers,
    sideMeshes,
    graphicLayerMap,
    graphicLayers,
    groupDecalSettings,
    lockedGroupDecals,
    pendingGroupTexture,
    setPendingGroupTexture,
    activeGroup,
    activeSideKey,
    activeSide,
    groupDecals,
    sideDecals,
    pendingTexture,
    setPendingTexture,
    lockedDecals,
    decalSettings,
    currentEditingGraphic,
    isDraggingDecal,
    setIsDraggingDecal,
    dragStarted,
    setDragStarted,
    dragOffset,
    setDragOffset
} from './state.js';
import {
    createGroupTriplanarDecal,
    createTriplanarDecal,
    updateAllMaterialsWithGraphicLayers,
    hideDecalHint,
    invalidateBorderPointsCache
} from './decalManager.js';
import { requestRender } from './sceneManager.js';
import { openGraphicForEditing } from './graphicsManager.js';
import { allowsDecals } from './utils.js';

// ===== POINTER DOWN HANDLER =====
export function onPointerDown(e, getMouse, setActiveSide, openPanel) {
    const mouse = getMouse(e);
    raycaster.setFromCamera(mouse, camera);

    // Reset drag state
    setDragStarted(false);

    // Flag to track if click was on an existing decal
    let clickedOnExistingDecal = false;

    // ===== CHECK FOR CLICKS ON PLACED GRAPHICS =====
    // Collect all meshes to raycast against
    const allMeshes = [];
    sideMeshes.forEach((groupSides) => {
        groupSides.forEach(meshSet => {
            meshSet.forEach(mesh => allMeshes.push(mesh));
        });
    });

    if (allMeshes.length > 0) {
        const hits = raycaster.intersectObjects(allMeshes, true);
        if (hits.length > 0) {
            const {uv, object} = hits[0];

            // Check if the click hit any placed graphic
            for (let i = 0; i < graphicLayers.length; i++) {
                const layer = graphicLayers[i];
                if (!layer || !layer.graphicId) continue;

                // Check if UV coordinates fall within this graphic's bounds
                const dx = uv.x - layer.centerUV.x;
                const dy = uv.y - layer.centerUV.y;

                // Apply rotation to get local coordinates
                const cos = Math.cos(-layer.rotation);
                const sin = Math.sin(-layer.rotation);
                const localX = dx * cos - dy * sin;
                const localY = dx * sin + dy * cos;

                // Check if within bounds
                const halfWidth = layer.sizeUV.x;
                const halfHeight = layer.sizeUV.y;

                if (Math.abs(localX) <= halfWidth && Math.abs(localY) <= halfHeight) {
                    // Click is within this graphic
                    // Wenn kein Grafik im Bearbeitungsmodus oder ein anderes Grafik: Öffnen
                    if (!currentEditingGraphic || currentEditingGraphic.id !== layer.graphicId) {
                        openGraphicForEditing(layer.graphicId);
                        return; // Stop processing the click
                    }
                    // Wenn bereits im Bearbeitungsmodus: Markiere dass auf Decal geklickt wurde
                    clickedOnExistingDecal = true;
                    break; // Aus der Loop raus, aber Click-Handling fortsetzen
                }
            }
        }
    }

    // ===== NORMAL CLICK HANDLING =====
    if (
        currentEditingGraphic === null ||
        !currentEditingGraphic.isEditing ||
        lockedGroupDecals.get(currentEditingGraphic?.id) === true
    ) {
        return;
    }

    hideDecalHint();

    const mhits = raycaster.intersectObjects(markerHelpers, true);
    if (mhits.length) {
        const hitHelper = mhits[0].object;
        const entry = [...markers.entries()].find(([_, m]) => m.helper === hitHelper);
        if (entry) {
            const [key, {group, side}] = entry;
            setActiveSide(key);
            openPanel(side, group);
            return;
        }
    }

    if (activeGroup) {
        const allGroupMeshes = [];
        const isGraphicId = activeGroup.startsWith && activeGroup.startsWith('graphic-');

        if (isGraphicId) {
            // Für Grafiken: Alle Configurator-Meshes durchsuchen
            sideMeshes.forEach((groupSides) => {
                groupSides.forEach(meshSet => {
                    meshSet.forEach(mesh => allGroupMeshes.push(mesh));
                });
            });
        } else {
            // Für Gruppen: Nur Meshes dieser Gruppe
            const groupSides = sideMeshes.get(activeGroup);
            if (groupSides) {
                groupSides.forEach(meshSet => {
                    meshSet.forEach(mesh => allGroupMeshes.push(mesh));
                });
            }
        }

        if (!allGroupMeshes.length) return;

        const hits = raycaster.intersectObjects(allGroupMeshes, true);
        if (!hits.length) return;

        const {point, face, object, uv} = hits[0];

        // Check if this object allows decal placement
        if (!allowsDecals(object)) {
            return;
        }

        const normWorld = face.normal.clone().transformDirection(object.matrixWorld);

        // Für Grafiken: Multi-Layer System (bis zu 8 Grafiken gleichzeitig)
        if (isGraphicId) {
            let layerIndex = graphicLayerMap.get(activeGroup);
            const isInitialPlacement = layerIndex === undefined;

            // Wenn die Grafik noch keinen Layer hat und nicht gelockt ist, platziere sie
            if (isInitialPlacement && !lockedGroupDecals.get(activeGroup)) {
                // Finde den höchsten belegten Layer
                let maxUsedIndex = -1;
                for (let i = 0; i < graphicLayers.length; i++) {
                    if (graphicLayers[i] !== null) {
                        maxUsedIndex = i;
                    }
                }

                // Finde einen freien Layer-Slot, bevorzuge Slots nach dem höchsten belegten
                layerIndex = -1;
                for (let i = maxUsedIndex + 1; i < graphicLayers.length; i++) {
                    if (graphicLayers[i] === null) {
                        layerIndex = i;
                        break;
                    }
                }

                // Falls kein Slot nach dem höchsten gefunden, suche von vorne
                if (layerIndex === -1) {
                    layerIndex = graphicLayers.findIndex(layer => layer === null);
                }

                if (layerIndex === -1) {
                    return;
                }
                graphicLayerMap.set(activeGroup, layerIndex);
            }

            // Platzierung/Umpositionierung
            if (layerIndex !== undefined && !lockedGroupDecals.get(activeGroup)) {
                const settings = groupDecalSettings.get(activeGroup) || {rotation: 0, scale: 1};
                const texture = pendingGroupTexture?.graphicId === activeGroup
                    ? pendingGroupTexture.texture
                    : graphicLayers[layerIndex]?.texture;

                if (!texture) {
                    return;
                }

                const img = texture.image;
                const aspectRatio = img ? img.width / img.height : 1;
                const baseSizeUV = 0.5;

                // Bei initialer Platzierung: Position setzen
                if (isInitialPlacement) {
                    graphicLayers[layerIndex] = {
                        texture: texture,
                        centerUV: new THREE.Vector2(uv.x, uv.y),
                        sizeUV: new THREE.Vector2(
                            baseSizeUV * aspectRatio * settings.scale,
                            baseSizeUV * settings.scale
                        ),
                        rotation: THREE.MathUtils.degToRad(settings.rotation),
                        aspectRatio: aspectRatio,
                        graphicId: activeGroup,
                        worldPosition: point.clone(),
                        hitMesh: object
                    };

                    if (pendingGroupTexture && pendingGroupTexture.graphicId === activeGroup) {
                        setPendingGroupTexture(null);
                    }

                    // Materialien aktualisieren
                    updateAllMaterialsWithGraphicLayers();

                    // UI aktualisieren
                    if (window._graphicUIUpdaters && window._graphicUIUpdaters.has(activeGroup)) {
                        window._graphicUIUpdaters.get(activeGroup)();
                    }

                    requestRender();
                }

                // Dragging vorbereiten wenn auf Decal geklickt wurde oder bei initialer Platzierung
                if (isInitialPlacement || clickedOnExistingDecal) {
                    const isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

                    // Auf Mobile: Kein sofortiges Dragging, nur vorbereiten
                    // Auf Desktop: Bei initialer Platzierung sofort dragging aktivieren
                    setDragStarted(true);

                    if (isInitialPlacement && !isMobile) {
                        // Desktop: Sofortiges Dragging bei initialer Platzierung
                        setIsDraggingDecal(true);
                        if (controls) {
                            controls.enabled = false;
                        }
                        // Bei initialer Platzierung: kein Offset
                        setDragOffset({ x: 0, y: 0 });
                    } else if (clickedOnExistingDecal && graphicLayers[layerIndex]) {
                        // Offset zwischen Mausposition und Decal-Zentrum berechnen (für später)
                        setDragOffset({
                            x: graphicLayers[layerIndex].centerUV.x - uv.x,
                            y: graphicLayers[layerIndex].centerUV.y - uv.y
                        });
                    } else if (isInitialPlacement && isMobile) {
                        // Mobile: Bei initialer Platzierung kein Offset (wird beim Drag gesetzt)
                        setDragOffset({ x: 0, y: 0 });
                    }
                }
            }
        } else {
            // Für Gruppen: Material-basierte Decals (wie bisher)
            const lastDecal = groupDecals.get(activeGroup);

            if (lastDecal && !lockedGroupDecals.get(activeGroup)) {
                const settings = groupDecalSettings.get(activeGroup) || {rotation: 0, scale: 1};
                const texture = lastDecal.material.map;

                if (lastDecal.dispose) {
                    lastDecal.dispose();
                }

                const newDecal = createGroupTriplanarDecal(
                    activeGroup,
                    point,
                    normWorld,
                    texture,
                    THREE.MathUtils.degToRad(settings.rotation),
                    settings.scale,
                    uv
                );

                groupDecals.set(activeGroup, newDecal);
                requestRender();
            } else if (pendingGroupTexture && pendingGroupTexture.group === activeGroup) {
                const decal = createGroupTriplanarDecal(
                    activeGroup,
                    point,
                    normWorld,
                    pendingGroupTexture.texture,
                    0,
                    1,
                    uv
                );

                if (decal) {
                    groupDecals.set(activeGroup, decal);
                    setPendingGroupTexture(null);
                    requestRender();
                }
            }
        }
        return;
    }

    if (!activeSideKey) return;

    const marker = markers.get(activeSideKey);
    if (!marker) return;

    const {side, group} = marker;

    const meshesForSide = sideMeshes.get(group)?.get(side);
    if (!meshesForSide || !meshesForSide.size) {
        return;
    }


    const hits = raycaster.intersectObjects([...meshesForSide], true);
    if (!hits.length) return;

    const {point, face, object} = hits[0];

    // Check if this object allows decal placement
    if (!allowsDecals(object)) {
        return;
    }

    const normWorld = face.normal.clone().transformDirection(object.matrixWorld);
    window._lastHit = {point: point.clone(), normal: normWorld.clone(), object};

    const lastDecal = sideDecals.get(side);

    if (lastDecal && !lockedDecals.get(side)) {
        const settings = decalSettings.get(side) || {rotation: 180, scale: 1};
        const texture = lastDecal.material.map;

        if (lastDecal.dispose) {
            lastDecal.dispose();
        }

        const newDecal = createTriplanarDecal(
            group,
            side,
            point,
            normWorld,
            texture,
            THREE.MathUtils.degToRad(settings.rotation),
            settings.scale
        );

        sideDecals.set(side, newDecal);
        requestRender();
    } else if (pendingTexture && pendingTexture.side === side) {
        const decal = createTriplanarDecal(
            group,
            side,
            point,
            normWorld,
            pendingTexture.texture,
            0,
            1
        );
        sideDecals.set(side, decal);
        setPendingTexture(null);
        requestRender();
    }
}

// ===== POINTER MOVE HANDLER (FOR DRAG & DROP) =====
export function onPointerMove(e, getMouse) {
    if (!dragStarted) return;
    if (!currentEditingGraphic || !currentEditingGraphic.isEditing) return;
    if (lockedGroupDecals.get(currentEditingGraphic.id)) return;

    // Beim ersten Move nach Click: Dragging aktivieren
    if (!isDraggingDecal) {
        setIsDraggingDecal(true);
        if (controls) {
            controls.enabled = false;
        }
    }

    const mouse = getMouse(e);
    raycaster.setFromCamera(mouse, camera);

    const isGraphicId = activeGroup && activeGroup.startsWith && activeGroup.startsWith('graphic-');
    if (!isGraphicId) return;

    // Sammle alle Meshes für Raycasting
    const allGroupMeshes = [];
    sideMeshes.forEach((groupSides) => {
        groupSides.forEach(meshSet => {
            meshSet.forEach(mesh => allGroupMeshes.push(mesh));
        });
    });

    if (!allGroupMeshes.length) return;

    const hits = raycaster.intersectObjects(allGroupMeshes, true);
    if (!hits.length) return;

    const {point, face, object, uv} = hits[0];

    // Check if this object allows decal placement (for dragging/repositioning)
    if (!allowsDecals(object)) {
        return;
    }

    const layerIndex = graphicLayerMap.get(activeGroup);

    if (layerIndex !== undefined) {
        const settings = groupDecalSettings.get(activeGroup) || {rotation: 0, scale: 1};
        const texture = graphicLayers[layerIndex]?.texture;

        if (!texture) return;

        const img = texture.image;
        const aspectRatio = img ? img.width / img.height : 1;
        const baseSizeUV = 0.2;

        // Update centerUV und worldPosition mit Offset
        graphicLayers[layerIndex].centerUV = new THREE.Vector2(
            uv.x + dragOffset.x,
            uv.y + dragOffset.y
        );
        graphicLayers[layerIndex].worldPosition = point.clone();
        graphicLayers[layerIndex].hitMesh = object;

        // Invalidate border points cache for this graphic (position changed)
        invalidateBorderPointsCache(activeGroup);

        // Materialien aktualisieren
        updateAllMaterialsWithGraphicLayers();
        requestRender();
    }
}

// ===== POINTER UP HANDLER (END DRAG & DROP) =====
export function onPointerUp() {
    if (dragStarted || isDraggingDecal) {
        // Kamera wieder aktivieren
        if (controls) {
            controls.enabled = true;
        }

        // Reset drag state
        setDragStarted(false);
        setIsDraggingDecal(false);
    }
}

// ===== GRAFIK-LAYER MANAGEMENT =====
// Note: Dummy texture and other functions are in decalManager.js
